import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, X, User, CircleNotch, Plus, Check } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

function Face({ src, name, size = 40 }) {
  return (
    <span className="rounded-full overflow-hidden bg-[#2a2a2a] border border-white/10 flex items-center justify-center flex-shrink-0 text-white/40" style={{ width: size, height: size }}>
      {src ? <img src={src} alt="" loading="lazy" referrerPolicy="no-referrer" className="w-full h-full object-cover" /> : <User size={size * 0.5} />}
    </span>
  );
}

/**
 * Find a person: first the ones already in the database, then Wikipedia (name, description, photo)
 * to add someone new. `onPick(person)` gets a database person either way.
 */
export default function PersonPicker({ onPick, suggestions = [], suggestionsTitle = "The cast", selectedId = "", placeholder = "Search an actor or celebrity…", autoFocus = false, testId = "person-picker" }) {
  const { user } = useAuth();
  const canAdd = (user?.roles || []).some((r) => r === "admin" || r === "scenepack_creator");
  const [q, setQ] = useState("");
  const [local, setLocal] = useState([]);
  const [wiki, setWiki] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState("");
  const seq = useRef(0);

  useEffect(() => {
    const term = q.trim();
    const my = ++seq.current;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const [a, b] = await Promise.all([
          api.get(`/people?limit=6&q=${encodeURIComponent(term)}`).then((r) => r.data.items).catch(() => []),
          canAdd && term.length >= 2 ? api.get(`/people/lookup?q=${encodeURIComponent(term)}`).then((r) => r.data.items).catch(() => []) : Promise.resolve([]),
        ]);
        if (my !== seq.current) return;
        setLocal(a);
        const have = new Set(a.map((p) => p.name.toLowerCase()));
        setWiki(b.filter((h) => !have.has(h.title.replace(/\s*\(.*\)$/, "").toLowerCase())));
      } finally {
        if (my === seq.current) setLoading(false);
      }
    }, term ? 300 : 0);
    return () => clearTimeout(t);
  }, [q, canAdd]);

  // someone from IMDb's cast list: make sure they're in the database (photo and bio included), then pick them
  const pickCast = async (c) => {
    setAdding(c.imdb_id);
    try {
      const { data } = await api.post("/people/from-imdb", { imdb_id: c.imdb_id, name: c.name });
      onPick(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't add them");
    }
    setAdding("");
  };

  const addFromWiki = async (hit) => {
    setAdding(hit.title);
    try {
      const { data } = await api.post("/people", { wiki_title: hit.title });
      onPick(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't add them");
    }
    setAdding("");
  };

  return (
    <div data-testid={testId}>
      <div className="relative mb-3">
        <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40 pointer-events-none" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus={autoFocus}
          placeholder={placeholder}
          autoComplete="off"
          data-testid={`${testId}-input`}
          className="!pl-11 !pr-10 !py-3 !rounded-full !text-base sm:!text-sm"
        />
        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-white/40">
          {loading ? <CircleNotch size={15} className="animate-spin" /> : q ? <button type="button" onClick={() => setQ("")} aria-label="Clear" className="hover:text-white"><X size={14} weight="bold" /></button> : null}
        </span>
      </div>

      <div className="rounded-xl border border-white/10 overflow-hidden divide-y divide-white/5 bg-[#131313] max-h-72 overflow-y-auto overscroll-contain">
        {suggestions.length > 0 && !q.trim() && (
          <>
            <div className="px-3 pt-2.5 pb-1 text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF8090]">{suggestionsTitle}</div>
            {suggestions.map((c) => (
              <button key={c.imdb_id} type="button" onClick={() => pickCast(c)} disabled={!!adding} data-testid={`${testId}-cast-${c.imdb_id}`} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.05] text-left transition disabled:opacity-60">
                <Face src={c.photo_url} name={c.name} />
                <span className="min-w-0 flex-1">
                  <span className="block font-display font-bold text-sm truncate">{c.name}</span>
                  <span className="block text-[11px] text-white/45 truncate">{c.characters || "—"}</span>
                </span>
                {adding === c.imdb_id ? <CircleNotch size={14} className="animate-spin text-white/50" /> : selectedId && c.person_id === selectedId ? <Check size={14} className="text-[#FF8090]" /> : null}
              </button>
            ))}
          </>
        )}
        {local.length > 0 && <div className="px-3 pt-2.5 pb-1 text-[10px] font-mono uppercase tracking-[0.25em] text-[#FF8090]">On ALPHA</div>}
        {local.map((p) => (
          <button key={p.id} type="button" onClick={() => onPick(p)} data-testid={`${testId}-person-${p.slug}`} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.05] text-left transition">
            <Face src={p.photo_url} name={p.name} />
            <span className="min-w-0 flex-1">
              <span className="block font-display font-bold text-sm truncate">{p.name}</span>
              <span className="block text-[10px] font-mono text-white/40">{p.packs} scenepack{p.packs !== 1 ? "s" : ""}</span>
            </span>
            {selectedId === p.id && <Check size={14} className="text-[#FF8090]" />}
          </button>
        ))}
        {wiki.length > 0 && <div className="px-3 pt-2.5 pb-1 text-[10px] font-mono uppercase tracking-[0.25em] text-white/40">Add from Wikipedia</div>}
        {wiki.map((h) => (
          <button key={h.title} type="button" onClick={() => addFromWiki(h)} disabled={!!adding} data-testid={`${testId}-wiki`} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/[0.05] text-left transition disabled:opacity-60">
            <Face src={h.photo} name={h.title} />
            <span className="min-w-0 flex-1">
              <span className="block font-display font-bold text-sm truncate">{h.title}</span>
              <span className="block text-[11px] text-white/45 truncate">{h.description}</span>
            </span>
            {adding === h.title ? <CircleNotch size={14} className="animate-spin text-white/50" /> : <Plus size={14} className="text-[#FF8090]" />}
          </button>
        ))}
        {!loading && local.length === 0 && wiki.length === 0 && (
          <div className="px-4 py-5 text-xs text-white/40 text-center">{q.trim().length < 2 ? "Type a name to search." : canAdd ? "Nobody found — try the full name." : "Nobody found."}</div>
        )}
      </div>
    </div>
  );
}
